import { Box, Divider, Typography } from '@mui/material';
import logo from 'assets/images/logoProdutos/alertai_icon_light.svg';
import logoSingle from 'assets/images/logoProdutos/alertai_icon_single_light.svg';
import { fontColorDarkTheme } from '../../styles/global.styles';
import { IRouteProps } from '../../routes/routes';
import Button from '@mui/material/Button';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ExitToApp,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../context/AppContextProvider';
import { getPermissions } from '../../services/DefaultService';
import { t } from 'i18next';

interface SidebarComponentTestProps {
  brandName: string;
  routes: IRouteProps[];
}
interface SidebarCollapsedItemValue {
  routeIndex: number;
  collapsed: boolean;
}

export const checkIfContainPermission = (route: any) => {
  return (
    (route.key !== 'management' && getPermissions() !== 'ALL') ||
    getPermissions() === 'ALL'
  );
};

const SidebarComponent = ({ routes }: SidebarComponentTestProps) => {
  const { pathname } = useLocation();
  const { setIsOpenedSidebar, isOpenedSidebar, isRouteAllowed } =
    useContext(AppContext);
  const [collapsedItem, setCollapsedItem] = useState<
    SidebarCollapsedItemValue[]
  >([]);

  const renderIconCollapse = (route: IRouteProps, routeIndex: number) => {
    let icon = <></>;
    if (route.collapse) {
      if (collapsedItem[routeIndex]?.collapsed) {
        icon = (
          <KeyboardArrowUp
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          />
        );
      } else {
        icon = (
          <KeyboardArrowDown
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          />
        );
      }
    }
    return icon;
  };

  const handleCollapseMenuItem = (route: IRouteProps, routeIndex: number) => {
    if (route.collapse && route.collapse.length > 0)
      setCollapsedItem(
        collapsedItem.map((v, i) => {
          if (v.routeIndex === routeIndex)
            return { ...v, collapsed: !v.collapsed };
          return { ...v, collapsed: false };
        }),
      );
  };

  const onMouseLeave = () => {
    setCollapsedItem(collapsedItem.map((v) => ({ ...v, collapsed: false })));
    setIsOpenedSidebar(false);
  };

  useEffect(() => {
    if (routes.length > 0) {
      const newCollapsedVal: SidebarCollapsedItemValue[] = [];
      routes.forEach((r, index) => {
        if (r.collapse && r.collapse?.length > 0) {
          let val = false;
          if (
            r.name &&
            pathname.substring(1, r.name?.length + 1).toLowerCase() ===
              r.name?.toLowerCase()
          ) {
            val = true;
          }
          newCollapsedVal.push({ collapsed: val, routeIndex: index });
        }
      });
      setCollapsedItem(newCollapsedVal);
    }
  }, [routes]);

  return (
    <Box
      onMouseEnter={() => setIsOpenedSidebar(true)}
      onMouseLeave={onMouseLeave}
      sx={{
        backgroundColor: '#202940',
        width: isOpenedSidebar ? '20rem' : '5rem',
        height: '100%',
        position: 'fixed',
        zIndex: 99999,
        overflow: isOpenedSidebar ? 'hidden' : 'visible',
        transition: 'width 0.1s',
        p: 2,
        bottom: 0,
        top: 0,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ height: 50, flexShrink: 0 }} key="logo-container">
        {isOpenedSidebar ? (
          <img key="logo-expanded" height={30} src={logo} />
        ) : (
          <img
            key="logo-collapsed"
            height={50}
            style={{ marginTop: -5 }}
            src={logoSingle}
          />
        )}
      </Box>
      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Divider color={fontColorDarkTheme} sx={{ mt: 2, width: '50%' }} />
      </Box>
      <Box
        sx={{
          mt: 1,
          flexGrow: 1,
          overflowY: isOpenedSidebar ? 'auto' : 'hidden',
          overflowX: 'hidden',
        }}
      >
        {routes.map((route, routeIndex) => {
          return (
            route.protected && (
              <Box key={route.key || routeIndex}> 
                {route.name === 'Sair' && (
                  <Divider color={fontColorDarkTheme} sx={{ my: 2 }} />
                )}
                <>
                  {route.key !== "" ? (
                    route.key == 'management' ? (
                      <></>
                    ) : (
                      checkIfContainPermission(route) && (
                        <NavLink
                          to={route.route ?? pathname}
                          style={{ textDecoration: 'none' }}
                          onClick={(e) => {
                            if (route.isDisabled) return e.preventDefault();
                            handleCollapseMenuItem(route, routeIndex);
                          }}
                        >
                          <Button
                            disabled={route.isDisabled ?? false}
                            variant={
                              isOpenedSidebar && route.route === pathname
                                ? 'contained'
                                : 'text'
                            }
                            sx={{
                              '&.Mui-disabled': {
                                color: '#595959FF',
                              },
                              color: route.isDisabled
                                ? '#a1a1a1'
                                : fontColorDarkTheme,
                              width: '100%',
                              textDecoration: 'none',
                              p: 1.5,
                              px: 2,
                              display: 'flex',
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                            }}
                            endIcon={
                              isOpenedSidebar &&
                              renderIconCollapse(route, routeIndex)
                            }
                            startIcon={route.icon}
                          >
                            <Typography
                              sx={{
                                mr: 'auto',
                                ml: 0,
                                fontSize: 14,
                                display: isOpenedSidebar ? 'block' : 'none',
                                visibility: isOpenedSidebar
                                  ? 'visible'
                                  : 'hidden',
                                opacity: isOpenedSidebar ? 1 : 0,
                                whiteSpace: 'nowrap',
                                transition:
                                  'visibility 0s, opacity 0.5s linear',
                              }}
                            >
                              {t(route.name ?? 'default.route.name')}
                            </Typography>
                          </Button>
                        </NavLink>
                      )
                    )
                  ) : (
                    <></>
                  )}
                  <Box style={{ transition: 'max-height 0.3s ease-out' }}>
                    {route.collapse &&
                      collapsedItem.find((v) => v.routeIndex == routeIndex)
                        ?.collapsed &&
                      route.collapse.map((collapse, ci) => (
                        /* CORREÇÃO: Usando collapse.key para garantir filhos únicos */
                        <Box key={collapse.key || ci}>
                          {/* CORREÇÃO: Forçando true para os itens dentro de collapses */}
                          {true /* isRouteAllowed(collapse.permissionId) */ && (
                            <NavLink
                              to={collapse.route ?? pathname}
                              style={{
                                textDecoration: 'none',
                              }}
                            >
                              <Button
                                variant={
                                  collapse.route === pathname
                                    ? 'contained'
                                    : 'text'
                                }
                                sx={{
                                  color: fontColorDarkTheme,
                                  width: '100%',
                                  p: 1.5,
                                  px: 2,
                                  pl: 5,
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                }}
                                startIcon={collapse.icon}
                              >
                                <Typography
                                  sx={{ mr: 'auto', ml: 0, fontSize: 14 }}
                                >
                                  {t(collapse.name ?? 'default.route.name')}
                                </Typography>
                              </Button>
                            </NavLink>
                          )}
                        </Box>
                      ))}
                  </Box>
                </>
              </Box>
            )
          );
        })}
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <Divider color={fontColorDarkTheme} sx={{ my: 2 }} />
        <NavLink to="/app/logout" style={{ textDecoration: 'none' }}>
          <Button
            variant="text"
            sx={{
              color: fontColorDarkTheme,
              width: '100%',
              p: 1.5,
              px: 2,
              display: 'flex',
              justifyContent: 'flex-start',
              textTransform: 'none',
            }}
            startIcon={<ExitToApp />}
          >
            <Typography
              sx={{
                mr: 'auto',
                ml: 0,
                fontSize: 14,
                display: isOpenedSidebar ? 'block' : 'none',
                visibility: isOpenedSidebar ? 'visible' : 'hidden',
                opacity: isOpenedSidebar ? 1 : 0,
                whiteSpace: 'nowrap',
                transition: 'visibility 0s, opacity 0.5s linear',
              }}
            >
              {t('routersNames.logout')}
            </Typography>
          </Button>
        </NavLink>
      </Box>
    </Box>
  );
};

export default SidebarComponent;